import { useState, type FormEvent } from 'react';
import { Link } from '../../../components/Link';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../../../lib/api';
import { Icon } from '../../../components/icons';
import { Button, Card, Field, Input, cn } from '../../../components/ui';
import { DeferredTurnstileWidget } from '../../../components/TurnstileWidget';
import { useTurnstileGate } from '../../../lib/useAbuseConfig';
import { CopyButton, ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type Tone = 'professional' | 'friendly' | 'direct' | 'casual' | 'persuasive';
type Kind = 'cold-outreach' | 'follow-up' | 'introduction' | 'reply' | 'announcement';

interface AiResult {
  result: string;
  meta: { provider: string; model: string; inputTokens: number; outputTokens: number; costMicrosUsd: number };
}

const TONES: Array<{ value: Tone; label: string }> = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'direct', label: 'Direct' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
];

const KINDS: Array<{ value: Kind; label: string }> = [
  { value: 'cold-outreach', label: 'Cold outreach' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'reply', label: 'Reply' },
  { value: 'announcement', label: 'Announcement' },
];

/**
 * AI Email Writer — LLM-powered free tool. Hits /public/tools/ai/email-writer,
 * which runs the request through the super-admin-configured provider+model
 * (default: Claude Haiku 4.5) and enforces a per-IP daily quota from settings.
 */
export function AiEmailWriterPage() {
  const [goal, setGoal] = useState('');
  const [audience, setAudience] = useState('');
  const [sender, setSender] = useState('');
  const [tone, setTone] = useState<Tone>('professional');
  const [kind, setKind] = useState<Kind>('cold-outreach');
  const [rateLimited, setRateLimited] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const gate = useTurnstileGate();

  const write = useMutation<AiResult, ApiError>({
    mutationFn: () =>
      api<AiResult>('/public/tools/ai/email-writer', {
        method: 'POST',
        body: { goal, audience: audience || undefined, sender: sender || undefined, tone, kind },
        auth: false,
        headers: gate.headers,
      }),
    onSuccess: () => {
      setRateLimited(null);
      setError(null);
    },
    onError: (e) => {
      if (e.status === 429) {
        setRateLimited(e.message);
        setError(null);
      } else {
        setError(e.message);
        setRateLimited(null);
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!goal.trim()) return;
    write.mutate();
  }

  const content: ToolPageContent = {
    slug: 'ai-email-writer',
    title: 'AI email writer',
    tagline: 'Tell us the goal — get a clean, human-sounding email back in seconds.',
    eyebrow: 'Free tool · AI-powered · Rate-limited per IP',
    description:
      'Free AI email writer. Generate clean, human-sounding cold outreach, follow-ups, intros, replies, and announcements in 4-6 sentences with the tone you pick.',
    icon: <Icon.Mail />,
    accent: 'accent',
    intro:
      "Tell us what the email needs to do, who it's for, and the tone you want. We generate a 4-6 sentence draft you can paste straight into Gmail or Outlook. No filler openers, no marketing slop.",
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Email brief</h2>
          <form onSubmit={onSubmit} {...gate.armProps} className="mt-5 grid gap-4">
            <Field label="What should the email do?" hint="Plain English, e.g. 'introduce our deliverability platform and book a 15-min call'.">
              <textarea
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Introduce our deliverability platform and book a 15-min call with the head of marketing."
                rows={4}
                maxLength={2000}
                className="block w-full rounded-md border border-border bg-bg p-3 text-[13.5px] text-txt placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                autoFocus
              />
            </Field>
            <Field label="Audience" hint="Optional. e.g. 'Head of email at a 50-person B2B SaaS'.">
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Head of marketing, B2B SaaS, 50-200 employees" maxLength={500} />
            </Field>
            <Field label="Sender" hint="Optional. e.g. 'co-founder of an email integrity platform'.">
              <Input value={sender} onChange={(e) => setSender(e.target.value)} placeholder="Co-founder of an email integrity platform" maxLength={500} />
            </Field>

            <Field label="Tone">
              <Segmented value={tone} onChange={setTone} options={TONES} />
            </Field>
            <Field label="Kind of email">
              <Segmented value={kind} onChange={setKind} options={KINDS} />
            </Field>

            <DeferredTurnstileWidget gate={gate} />
            <Button type="submit" variant="primary" disabled={write.isPending || !goal.trim() || !gate.ready}>
              {write.isPending ? 'Writing…' : 'Generate email'}
            </Button>
            {error && <p className="text-[13px] text-review">{error}</p>}
            {rateLimited && (
              <p className="text-[13px] text-verify">
                {rateLimited} <Link to="/register" className="text-accent hover:underline">Create a workspace</Link> for unlimited generations.
              </p>
            )}
          </form>
        </Card>

        <Card className="p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Draft</h2>
            {write.data && <CopyButton text={write.data.result} />}
          </div>
          {!write.data && (
            <div className="mt-4 flex h-64 items-center justify-center text-center text-[13px] text-muted">
              Fill in the brief on the left, then hit Generate.
            </div>
          )}
          {write.data && (
            <>
              <pre className="mt-4 whitespace-pre-wrap break-words rounded-md border border-bordersoft bg-panel2 p-4 text-[13.5px] leading-relaxed text-txt">
                {write.data.result}
              </pre>
              <p className="mt-3 text-[11.5px] text-faint">
                Generated by {write.data.meta.provider} / {write.data.meta.model} · {write.data.meta.inputTokens + write.data.meta.outputTokens} tokens
              </p>
            </>
          )}
        </Card>
      </div>
    ),
    educational: {
      title: 'When AI email drafts actually help',
      paragraphs: [
        'The biggest mistake we see with AI-written email is over-instruction — long, structured prompts asking for "high-converting cold outreach with three pain points and a soft CTA." That produces template-shaped marketing prose every spam filter recognises and every recipient ignores.',
        'The opposite works. State the goal plainly, name the audience, set a human tone, and let the model write a short, direct email. We deliberately cap output to 4-6 sentences because that\'s what mailbox providers and humans both reward — shorter, more personal mail outperforms long blasts on every metric we\'ve seen.',
        'This tool is a draft engine, not a sender. Always read what comes out, edit anything that sounds off, and send from your own warmed mailbox. AI-written cold emails sent from a cold mailbox land in spam regardless of how well-written they are — that\'s what mailbox warmup tools fix on the other side.',
      ],
    },
    faq: [
      { q: 'Does the email count as written "by" me?', a: 'You\'re the human deciding what gets sent. Use the draft as a starting point — edit one or two sentences so it sounds like you, not us.' },
      { q: 'Why only 4-6 sentences?', a: 'Cold-outreach data from every reputable source (Outreach, Lavender, every public deliverability dataset) shows opens + replies peak at short emails. We enforce the cap in the prompt.' },
      { q: 'Will Gmail know it was AI-written?', a: 'Gmail\'s filters don\'t classify "AI-written"; they classify "spammy patterns." Generic prose with classic spam triggers gets caught regardless of source. Specific, human-sounding prose doesn\'t — this tool aims for the latter.' },
      { q: 'Can I generate longer emails?', a: 'Not in the free version. The constraint is a feature, not a limitation — we deliberately bias toward what works for cold outreach. Inside the platform, longer drafts are available via the Inbox Placement and Warmup add-ons.' },
      { q: 'Where does the request go?', a: "A SieveGuard endpoint that routes to our AI assistant. The text you submit and the draft you receive aren't stored." },
    ],
    relatedTools: [
      { slug: 'subject-line-generator', title: 'Subject line generator', tagline: 'Generate 7 subject-line variants for any email.' },
      { slug: 'email-shortener', title: 'Email shortener', tagline: 'Take a verbose draft and tighten it up.' },
      { slug: 'subject-line-tester', title: 'Subject line tester', tagline: 'Grade an existing subject line A–F.' },
    ],
    ctaTitle: 'Send the email from a mailbox that lands',
    ctaBody:
      "A perfectly-written email still hits spam if it's sent from a cold mailbox. SieveGuard's Mailbox Warmup add-on auto-warms your sending inboxes via partner send/reply loops so cold outreach actually lands.",
  };

  return <ToolPageLayout content={content} />;
}

interface SegOption<T extends string> {
  value: T;
  label: string;
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegOption<T>[];
}) {
  return (
    <div className="inline-flex flex-wrap rounded-md border border-border bg-bg p-1 text-[12.5px]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-3 py-1.5 transition',
            value === o.value ? 'bg-accent text-[#04141d] font-semibold' : 'text-muted hover:bg-hover hover:text-txt',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Default export required by React Router framework-mode route modules.
export default AiEmailWriterPage;
